import { planCoversTreatmentAndService as resolvePlanCoversTreatmentAndService, resolveCarePlusShipping } from "./carePlusCartTotals";
import { CarePlusCart, getCarePlusShippingCartProps } from "./carePlusShippingCartProps";

export type CarePlusCostBreakdownInput = {
  /** Plan fee (`care_plus_plan_fee`); > 0 collapses treatment/service/delivery into one plan row. */
  planFee: number;
  planTier: string;
  /** Plan row amounts: plan fee + delivery, charged net of server-waived fees. */
  planListPrice: number;
  planChargedPrice: number;
  medicationTotal: number;
  treatmentListPrice: number;
  treatmentChargedPrice: number;
  serviceFeeListPrice: number;
  serviceFeeChargedPrice: number;
  deliveryListPrice: number;
  deliveryChargedPrice: number;
  discount: number;
  isRecurringDiscount: boolean;
};

/**
 * HL-11359 — list/charged amounts for the "Kostenaufstellung" cost breakdown.
 *
 * Built on top of the cart card's numbers (getCarePlusShippingCartProps) so the breakdown always
 * sums to the "Final price today" shown right above it: same medication line, same delivery fee,
 * same discount. This module only splits the plan fee into its list vs charged parts.
 *
 * Plan list fees come from the plan fee breakdown × prepaid months. Charged 0 only when Pro/Max
 * covers the fee or the server waived it. Server-waived fees are already shown struck through, so
 * the discount row lists only the remainder of the cart discount (voucher, recurring coupon);
 * otherwise that money would appear twice.
 */
export const getCarePlusCostBreakdownInput = (cart: CarePlusCart): CarePlusCostBreakdownInput => {
  const { cardProps } = getCarePlusShippingCartProps(cart, true);
  const isCarePlusPlanOrder = cardProps.planFee > 0;
  const periodMonths = cardProps.periodMonths;
  const breakdown = cart.care_plus_plan_fee_breakdown;
  const waivedFees = cart.care_plus_waived_fees ?? undefined;
  const planCoversTreatmentAndService = resolvePlanCoversTreatmentAndService(cardProps.planFee, cardProps.planTier);

  const deliveryListPrice = cardProps.shippingFee;
  const deliveryChargedPrice = resolveCarePlusShipping(deliveryListPrice, waivedFees).fee;

  let treatmentListPrice: number;
  let treatmentChargedPrice: number;
  let serviceFeeListPrice: number;
  let serviceFeeChargedPrice: number;

  if (isCarePlusPlanOrder) {
    treatmentListPrice = Number(breakdown?.treatment_fee ?? 0) * periodMonths;
    serviceFeeListPrice = Number(breakdown?.service_fee ?? 0) * periodMonths;
    treatmentChargedPrice =
      planCoversTreatmentAndService || waivedFees?.includes("treatment_fee")
        ? 0
        : treatmentListPrice;
    serviceFeeChargedPrice =
      planCoversTreatmentAndService || waivedFees?.includes("service_fee")
        ? 0
        : serviceFeeListPrice;
  } else {
    // One-time order: a fee bundled into the displayed medication price (displayed_price_components)
    // has no row of its own, exactly like the card.
    treatmentListPrice = cart.only_prescription
      ? Number(cart.treatment_fee ?? 0)
      : cardProps.showTreatmentFee ? cardProps.treatmentFee : 0;
    serviceFeeListPrice = cardProps.showServiceFee ? cardProps.serviceFee : 0;
    treatmentChargedPrice = waivedFees?.includes("treatment_fee") ? 0 : treatmentListPrice;
    serviceFeeChargedPrice = waivedFees?.includes("service_fee") ? 0 : serviceFeeListPrice;
  }

  const struckByWaiver =
    (waivedFees?.includes("treatment_fee") ? treatmentListPrice - treatmentChargedPrice : 0)
    + (waivedFees?.includes("service_fee") ? serviceFeeListPrice - serviceFeeChargedPrice : 0)
    + (deliveryListPrice - deliveryChargedPrice);
  const discount = Math.max(0, Math.round((cardProps.discount - struckByWaiver) * 100) / 100);

  // Plan order renders one plan row (plan fee + delivery). Server-waived plan fees (pill first
  // month) come off its charged amount so the waiver is still struck through, not lost.
  const waivedPlanFees = isCarePlusPlanOrder
    ? (waivedFees?.includes("treatment_fee") ? treatmentListPrice : 0)
      + (waivedFees?.includes("service_fee") ? serviceFeeListPrice : 0)
    : 0;

  return {
    planFee: cardProps.planFee,
    planTier: cardProps.planTier,
    // planRowFee: 0 when the plan's fees sit in the medication price (HL-12639 UK Monthly).
    planListPrice: cardProps.planRowFee + deliveryListPrice,
    planChargedPrice: Math.max(0, cardProps.planRowFee - waivedPlanFees) + deliveryChargedPrice,
    medicationTotal: cardProps.medicationTotal,
    treatmentListPrice,
    treatmentChargedPrice,
    serviceFeeListPrice,
    serviceFeeChargedPrice,
    deliveryListPrice,
    deliveryChargedPrice,
    discount,
    isRecurringDiscount: cardProps.discountType === "recurring",
  };
};
